export type PayloadContactRequirements = {
  requirePhone: boolean;
  requireEmail: boolean;
  legacyRequirePhoneOrEmail: boolean;
};

export type PayloadRequirementConfigUpdate = {
  path: string[];
  value: boolean;
};

export const REQUIRED_PAYLOAD_FIELD_OPTIONS = [
  { key: "external_lead_id", label: "External lead ID" },
  { key: "source_name", label: "Source name" },
  { key: "source_type", label: "Source type" },
  { key: "first_name", label: "First name" },
  { key: "last_name", label: "Last name" },
  { key: "address_line1", label: "Street address" },
  { key: "address_line2", label: "Address line 2" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "postal_code", label: "ZIP / postal code" },
  { key: "country", label: "Country" },
  { key: "project_type", label: "Project type" },
  { key: "trade", label: "Trade" },
  { key: "lead_cost", label: "Lead cost" },
  { key: "budget", label: "Customer budget" },
  { key: "trustedform_cert_url", label: "TrustedForm certificate" },
] as const;

export type RequiredPayloadField =
  (typeof REQUIRED_PAYLOAD_FIELD_OPTIONS)[number]["key"];

const REQUIRED_PAYLOAD_FIELD_KEYS = new Set<string>(
  REQUIRED_PAYLOAD_FIELD_OPTIONS.map((field) => field.key),
);

function getPayloadValidationConfig(
  config: Record<string, unknown> | null,
): Record<string, unknown> {
  const payloadValidation = config?.payload_validation;
  return payloadValidation &&
      typeof payloadValidation === "object" &&
      !Array.isArray(payloadValidation)
    ? payloadValidation as Record<string, unknown>
    : {};
}

export function getPayloadContactRequirements(
  config: Record<string, unknown> | null,
): PayloadContactRequirements {
  const payloadValidation = getPayloadValidationConfig(config);
  const hasExplicitRequirements =
    typeof payloadValidation.require_phone === "boolean" ||
    typeof payloadValidation.require_email === "boolean";

  return {
    requirePhone: payloadValidation.require_phone === true,
    requireEmail: payloadValidation.require_email === true,
    legacyRequirePhoneOrEmail:
      !hasExplicitRequirements &&
      payloadValidation.require_phone_or_email !== false,
  };
}

export function buildPayloadContactRequirementUpdates(
  current: PayloadContactRequirements,
  requirement: "phone" | "email",
  checked: boolean,
): PayloadRequirementConfigUpdate[] {
  return [
    {
      path: ["payload_validation", "require_phone"],
      value: requirement === "phone" ? checked : current.requirePhone,
    },
    {
      path: ["payload_validation", "require_email"],
      value: requirement === "email" ? checked : current.requireEmail,
    },
    {
      path: ["payload_validation", "require_phone_or_email"],
      value: false,
    },
  ];
}

export function getRequiredPayloadFields(
  config: Record<string, unknown> | null,
): RequiredPayloadField[] {
  const configured = getPayloadValidationConfig(config).required_fields;
  if (!Array.isArray(configured)) return [];

  return [...new Set(
    configured.filter(
      (field): field is RequiredPayloadField =>
        typeof field === "string" &&
        REQUIRED_PAYLOAD_FIELD_KEYS.has(field),
    ),
  )];
}

export function toggleRequiredPayloadField(
  current: RequiredPayloadField[],
  field: RequiredPayloadField,
  required: boolean,
): RequiredPayloadField[] {
  if (required) {
    return current.includes(field) ? current : [...current, field];
  }
  return current.filter((configuredField) => configuredField !== field);
}
